/**
 * feat-241 — the manager-facing vocabulary of the curriculum-coverage surfaces.
 *
 * AC19: FE vocabulary in this repo renders through CONSTANTS, not literals — a
 * literal-text grep returns a TRUE ZERO for copy that is visibly on screen. Every
 * string a manager can read on the matrix or the passport appears in this module
 * and nowhere else, and the tests import the constant rather than spelling "Open".
 *
 * Design authority: `design/state-cells.md` §5 (copy) and §1 (the four states).
 */

/** The four states the backend can return for a (crew, track) pair. */
export type CellStatus = 'completed' | 'in_progress' | 'available' | 'locked';

/**
 * The manager-facing name of each coverage state.
 *
 * `in_progress` is the odd one out: its VISIBLE label is the `N of M` fraction
 * (P7 — the fraction carries the meaning without colour), so this string is
 * rendered `sr-only` ahead of the fraction. Without it a screen-reader user
 * hears a bare number in a grid of bare numbers.
 *
 * `available` reads "Open", not "Not started" — the word the approved concept and
 * the program file both use, and the word slice 3 already uses for
 * `access_mode: 'open'`, so manager copy and schema vocabulary agree instead of
 * drifting (design OPEN-QUESTIONS R2).
 */
export const CELL_LABEL: Record<CellStatus, string> = {
  completed: 'Done',
  in_progress: 'In progress',
  available: 'Open',
  locked: 'Locked',
};

/**
 * The in-progress fraction. The word "of", never `3/7`: spelled out it survives
 * being read aloud and cannot be mistaken for a date or a score.
 *
 * `total` comes from `track.lessons_total` (P14 — the TRACK object, never the
 * cell); `done` from `cell.lessons_complete`.
 */
export const CELL_FRACTION = (done: number, total: number): string => `${done} of ${total}`;

/**
 * A locked cell's second line. `first` is the title of the leftmost blocking
 * column (design R3 — `tracks` order, NOT the order the backend happens to put
 * `blocking_track_ids` in, and NOT sorted by title); `more` is the count of
 * ADDITIONAL blockers.
 *
 * A title is never abbreviated into a new string — a manager must be able to
 * match the word in the cell to the word in the column header character for
 * character (AC16).
 */
export const CELL_BLOCKED_BY = (first: string, more: number): string =>
  more > 0 ? `Needs ${first} +${more}` : `Needs ${first}`;

/** The full blocker list, for the locked cell's native `title` attribute. */
export const CELL_BLOCKED_BY_FULL = (titles: readonly string[]): string =>
  `Needs ${titles.join(', ')}`;

/**
 * feat-302 — a track that is off-curriculum for this crew member (audience
 * scope, `cell.applies === false`). A muted dash with no state colour and no
 * progress; the words go in the native `title` and an sr-only name.
 */
export const CELL_NOT_ASSIGNED_MARK = '—';
export const CELL_NOT_ASSIGNED = (firstName: string): string =>
  firstName ? `Not assigned to ${firstName}` : 'Not assigned';
/** feat-302 D2 — the legend under the matrix, shown only when some cell is "—". */
export const CELL_NOT_ASSIGNED_FOOTNOTE = "— Not assigned: outside this person's locations or roles.";
/** The first word of a crew member's name, for {@link CELL_NOT_ASSIGNED}. */
export const crewFirstName = (name: string): string => name.trim().split(/\s+/)[0] ?? '';

export const COVERAGE_TAB_LABEL = 'Curriculum Coverage';
export const COVERAGE_PAGE_TITLE = 'Curriculum Coverage';
export const COVERAGE_PAGE_SUBTITLE =
  'Which of your crew have finished which stations, and who is one track away.';

export const COVERAGE_EMPTY_TITLE = 'No crew to show';
export const COVERAGE_EMPTY_BODY =
  'This view lists every crew member you can see. Try clearing the location filter.';
export const COVERAGE_ERROR_TITLE = "Couldn't load curriculum coverage";

/**
 * The error state's body copy and its retry (design F5).
 *
 * `state-cells.md` §5 listed only `COVERAGE_ERROR_TITLE`, so the body and the
 * retry were never built — a spec gap, not an oversight in either direction.
 * Wording is taken verbatim from `design/curriculum-coverage-matrix.html` §4e.
 *
 * THIS STATE IS REACHABLE ON PURPOSE during the rollout window. The spec's own
 * sequencing says the backend PR may merge first and that this surface "degrades
 * to an error state without it", so a manager can genuinely land here. The second
 * sentence therefore names that possibility in words they can act on — tell
 * someone — rather than showing a bare failure.
 */
export const COVERAGE_ERROR_BODY =
  'Something went wrong fetching the matrix. Try again — if it keeps happening, the coverage service may not be deployed yet.';
export const COVERAGE_ERROR_RETRY = 'Try again';

export const COVERAGE_LOADING = 'Loading curriculum coverage…';

/** Sticky-column headers. */
/**
 * Sticky-column headers. All three are sort controls (P9 — "every column is
 * sortable" was Preston's call over the narrower "summary column only"), so all
 * three are rendered, not just `COL_CREW`.
 *
 * `COL_LOCATION` and `COL_TRACKS_COMPLETE` had ZERO importers until QA F1: the
 * sort logic existed and was unit-tested for both, but no control emitted either
 * key. Two dead header constants were the corroborating structural tell.
 *
 * `COL_TRACKS_COMPLETE` reads "Tracks complete", matching the sub-line the mockup
 * shows under the crew header ("Location · tracks complete"); the header band
 * renders it `normal-case`, so the casing here is the label, not the styling.
 */
export const COL_CREW = 'Crew';
export const COL_LOCATION = 'Location';
export const COL_TRACKS_COMPLETE = 'Tracks complete';

export const TRACKS_COMPLETE = (n: number, total: number): string => `${n} of ${total} tracks`;
export const LESSON_COUNT = (n: number): string => (n === 1 ? '1 lesson' : `${n} lessons`);

// ---------- Passport (the `:serverId` drill-down) ----------

export const PASSPORT_BACK = 'All crew';
export const PASSPORT_NEXT_LESSON_PREFIX = 'Next:';
export const PASSPORT_LESSONS = (done: number, total: number): string =>
  `${done} of ${total} lessons`;

/**
 * The passport groups its cards by state (design R4), in AC15's order — so the
 * already-pinned order is reused rather than a second one invented.
 *
 * These are the GROUP headings and they are deliberately NOT `CELL_LABEL`: a
 * group of finished tracks is headed "Completed", while the chip on each card
 * inside it reads "Done". The heading names a collection; the chip names a
 * state.
 */
export const PASSPORT_GROUP_LABEL: Record<CellStatus, string> = {
  completed: 'Completed',
  in_progress: 'In progress',
  available: 'Open',
  locked: 'Locked',
};
/**
 * design R6 — ONE message for a `:serverId` absent from the response, whether the
 * crew member was removed or is at a location this manager cannot see.
 * Distinguishing the two would turn a shareable URL (P5) into a tenant-membership
 * oracle, and this feature already carries an RBAC risky-change flag.
 */
export const PASSPORT_NOT_FOUND_TITLE = "We couldn't find that crew member";
export const PASSPORT_NOT_FOUND_BODY =
  'They may have been removed, or they may be at a location you don’t have access to.';

// ---------- Sorting ----------

export const SORT_ASC = 'ascending';
export const SORT_DESC = 'descending';

// ---------- The in-progress ink — ONE reference (FRONTEND-SLICE §4 RULING 2) ----------

/**
 * The `in_progress` ink and progress-bar fill, reachable through a SINGLE named
 * reference.
 *
 * WHY THIS IS AN OBJECT AND NOT FOUR SPRINKLED CLASS STRINGS: `gold.on #8A5A11`
 * sits very close to feat-242-prereq-taxonomy-view's `attention-on #805E08` on
 * `attention-surface #FFF4D6` for HAZARD marks — ΔE76 5.15 on the surfaces and
 * 7.55 on the inks, the closest pair in TOKENS.md by a factor of two, carrying
 * OPPOSITE meanings ("this is fine, it's underway" vs "this is a problem").
 *
 * That collision was RULED by Preston on 2026-08-16 (TOKENS.md §1o): SHIP BOTH
 * AS-IS. It is a DECISION, not an open item, and must not be re-escalated. The
 * three rules in §1o are the STANDING state, not an interim one: neither family
 * may carry the other's meaning; the two are told apart by GLYPH AND COPY, never
 * hue alone — which this design already does; and a THIRD consumer reaching for
 * dark-gold-on-pale-gold must pick one of the two and say which. Being that third
 * consumer is itself the trigger to reopen the normalisation.
 *
 * This object stays a single reference regardless: it is what makes "`gold.on`
 * moves for one edit" true, and TOKENS.md §1o cites this file by name as the
 * evidence for that half of the symmetry. Do not sprinkle `text-gold-on` across
 * the tree.
 *
 * The strings are STATIC LITERALS, never interpolated: Tailwind purges classes it
 * cannot see as whole tokens in the source, and a purged class fails SILENTLY as
 * an unstyled render (`StatusChip.tsx`, design/state-cells.md §6).
 */
export const IN_PROGRESS_INK = {
  /** Ink for the `N of M` fraction. 5.274:1 on gold.light. */
  text: 'text-gold-on',
  /** The filled portion of the bar. Graphic floor is 3:1; this is 5.274:1. */
  barFill: 'bg-gold-on',
  /** The unfilled track behind the bar, and the chip's own fill. */
  barTrack: 'bg-gold-light',
  chipFill: 'bg-gold-light',
  /**
   * Where these two colours live in `tailwind.config.js`, so the AC17 contrast
   * test resolves them instead of hardcoding a copy that cannot drift with the
   * config. Kept beside the class strings deliberately: if the collision ruling
   * moves the token, the test follows in the same edit.
   */
  inkTokenPath: ['gold', 'on'] as const,
  surfaceTokenPath: ['gold', 'light'] as const,
} as const;
